import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';

/** A single editable season-period row. `clientId` is a stable key for @for tracking. */
export interface SeasonPeriodRow {
  id: number;
  periodTypeId: number;
  fromWeek: number;
  fromDay: number;
  untilWeek: number;
  untilDay: number;
  clientId: string;
}

/** Builds a fresh row with no season selected (0), defaulting to week 1 / Monday. */
export function newSeasonPeriodRow(): SeasonPeriodRow {
  return {
    id: 0,
    periodTypeId: 0,
    fromWeek: 1,
    fromDay: 1,
    untilWeek: 1,
    untilDay: 1,
    clientId: crypto.randomUUID(),
  };
}

/** True when the row's start position comes after its end position, i.e. it runs over the year end. */
export function seasonPeriodRowWrapsYearEnd(row: SeasonPeriodRow): boolean {
  return row.fromWeek > row.untilWeek || (row.fromWeek === row.untilWeek && row.fromDay > row.untilDay);
}

/**
 * A row is valid once a season type has been chosen (week/day always have a value) and, on the
 * exceptions tab, once its range does not run backwards.
 *
 * A backwards range (week 45 → week 8) is a period wrapping the year end. On the Periods tab that is
 * supported and is how a winter season or closure is entered — one row, evaluated with an OR test by
 * both the API and the legacy calendar. On the Exceptions tab it stays invalid: an exception applies
 * to a single year, where the same shape would mean two disjoint arcs (early January plus late
 * December) rather than a winter, so it must be entered as two exceptions in consecutive years.
 *
 * `allowWrap` is required rather than defaulted: the natural call is `rows.every(isSeasonPeriodRowValid)`,
 * and with a default that silently passes the array index in as `allowWrap` — always truthy from the
 * second row on. Requiring it turns that into a compile error.
 */
export function isSeasonPeriodRowValid(row: SeasonPeriodRow, allowWrap: boolean): boolean {
  if (row.periodTypeId <= 0) return false;
  return allowWrap || !seasonPeriodRowWrapsYearEnd(row);
}

/**
 * Presentational editable table of season periods (season type + from/to week+day + delete),
 * with an "Add period" button. Shared by the Periods and Exceptions tabs.
 */
@Component({
  selector: 'app-season-period-table',
  templateUrl: './season-period-table.component.html',
  styleUrl: './season-period-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTableModule, MatButtonModule, MatIconModule, MatSelectModule, MatTooltipModule, TranslatePipe],
})
export class SeasonPeriodTableComponent {
  readonly rows = input.required<SeasonPeriodRow[]>();
  readonly rowsChange = output<SeasonPeriodRow[]>();

  readonly codeListsService = inject(CodeListsService);

  readonly displayedColumns = ['seasonType', 'from', 'until', 'actions'];
  readonly weeks = Array.from({ length: 53 }, (_, i) => i + 1);
  readonly days = [
    { value: 1, key: 'monday' },
    { value: 2, key: 'tuesday' },
    { value: 3, key: 'wednesday' },
    { value: 4, key: 'thursday' },
    { value: 5, key: 'friday' },
    { value: 6, key: 'saturday' },
    { value: 7, key: 'sunday' },
  ];

  add(): void {
    // New rows start without a season; the user must pick one via the per-row dropdown before saving.
    this.rowsChange.emit([...this.rows(), newSeasonPeriodRow()]);
  }

  remove(index: number): void {
    this.rowsChange.emit(this.rows().filter((_, i) => i !== index));
  }

  setField(index: number, field: keyof SeasonPeriodRow, value: number): void {
    this.rowsChange.emit(this.rows().map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }
}
